import { FloorPlan, Order, TableStatus } from "../../types/index";
import { getAPIClient, routingFetch } from "../helpers";
import { ordersAPI } from "./orders";

export const tablesAPI = {
  getFloorPlans: async () => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.floor_plans.$get();
    if (resp.ok) {
      const data = await resp.json();
      return data as unknown as FloorPlan[];
    }
    throw new Error("Unable to load floor plans");
  },
  saveTableNote: async (tableId: number, name: string | undefined | null) => {
    const req = await routingFetch("/api/tables/parties", {
      method: "POST",
      body: JSON.stringify({
        table_id: tableId,
        name: name || null,
      }),
    });
    if (req.ok) {
      return;
    } else {
      throw new Error("unable to save note");
    }
  },
  createFloorPlan: async (attrs: Omit<FloorPlan, "id">): Promise<FloorPlan> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.floor_plans.$post({
      json: {
        name: attrs.name,
        rows: attrs.rows,
        cols: attrs.columns,
        plan: attrs.plan,
      },
    });
    if (!resp.ok) {
      throw new Error("Failed to create floor plan");
    }
    return (await resp.json()) as unknown as FloorPlan;
  },
  deleteFloorPlan: async (id: string) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.floor_plans[":id"].$delete({
      param: { id },
    });
    if (!resp.ok) {
      throw new Error("Failed to create floor plan");
    }
    return await resp.json();
  },
  updateFloorPlan: async (id: string, attrs: FloorPlan): Promise<FloorPlan> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.floor_plans[":id"].$put({
      param: { id },
      json: attrs,
    } as any);
    if (!resp.ok) {
      throw new Error("Failed to update floor plan");
    }
    return (await resp.json()) as unknown as FloorPlan;
  },
  getTableOrders: async (id: number): Promise<Order[]> => {
    const tables = await tablesAPI.getTablesMeta();
    const table = tables.find((el) => el.id === id);
    if (!table || !table.orders) return [];
    const orderIds = table?.orders.map((el) => el.id);
    if (!orderIds || orderIds.length === 0) return [];
    const orders = await ordersAPI.getOrders(orderIds);
    return orders;
  },
  getTablesMeta: async (): Promise<TableStatus[]> => {
    const hc = getAPIClient();
    const response = await hc.api.tables.$get();
    if (!response.ok) {
      throw new Error("Failed to fetch tables");
    }
    return response.json();
  },
  changeTable: async (
    tableId: number,
    targetTableId: number,
    force: boolean,
  ): Promise<boolean> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.tables.change.$post({
      json: {
        tableId,
        targetTableId,
        force,
      },
    });

    if (resp.ok) {
      return true;
    }

    if (resp.status === 422) {
      return false;
    }

    let message = "Failed to change table";
    try {
      const body = (await resp.json()) as { message?: string };
      if (body?.message) {
        message = body.message;
      }
    } catch {
      // ignore parsing errors and use default message
    }

    throw new Error(message);
  },
  closeTable: async (tableId: number) => {
    const hc = getAPIClient();
    const payload = {
      tableId,
    };
    const response = await hc.api.tables.close.$post({
      json: payload as any,
    });
    if (!response.ok) {
      throw new Error("Failed to closeTable");
    }
  },
  toggleTableBusy: async (tableId: number, currentStatus: number) => {
    const hc = getAPIClient();
    const payload = {
      tableId,
      currentStatus,
    };
    const response = await hc.api.tables.toggle.$post({
      json: payload as any,
    });
    if (!response.ok) {
      const error = (await response.json()) as { error?: string };
      throw new Error(error.error || "Failed to toggle table status");
    }
    return await response.json();
  },
};
